import {
    type EligibleProduct,
    useCallDetailsStore,
    useIntegratedFlowStore,
    useProductOfferStore,
} from "@expert/sales-tools";
import { Divider, Flex, SimpleGrid, Stack, Text } from "@mantine/core";

import { usePanelsStore } from "@/panels/modules/panels/store/panels.store";
import { EligibilityStatus } from "@/sales-tools/home-product/types";
import { CompactPlanCard } from "@/sales-tools/unified-sales-view/components/CompactPlanCard";
import { FullPlanCard } from "@/sales-tools/unified-sales-view/components/FullPlanCard";
import { usePartner, useSessionId } from "@/sdk";
import { useFeatureFlag, userCache } from "@/shared-utils";

import { NoOfferButton } from "./NoOfferButton";
import {
    categorizeProductsByType,
    formatIneligibleProductDisplay,
    getProductCardVariant,
    shouldShowNoOfferButton,
} from "./OffersView.helpers";
import classes from "./OffersView.module.css";

export function OffersView({ isChat = false }: { isChat?: boolean }) {
    const partner = usePartner();
    const sessionId = useSessionId();
    const { getProducts, updateSelectedProduct, getSelectedProduct, getProductInfo } = useProductOfferStore();
    const products = getProducts(sessionId);
    const { data: isVerizonCareEnabled } = useFeatureFlag("isVerizonCareEnabled", { partner });
    const panelsStore = usePanelsStore();

    const { CLIENT } = useCallDetailsStore.getState().callDetails;

    const selectedProduct = getSelectedProduct(sessionId);

    // Get categorized products
    const { primary, secondary, ineligible } = categorizeProductsByType({
        products,
        partner,
        isVerizonCareEnabled: isVerizonCareEnabled ?? false,
        pilots: userCache.pilots,
        client: CLIENT ?? "",
    });

    const handleCardClick = (sessionId: string, product: EligibleProduct) => {
        updateSelectedProduct(sessionId, product);
        panelsStore.selectFlowsTab();
    };

    const showIneligibleText = products.length > 0 && !products.some((p) => p.status === EligibilityStatus.Eligible);

    return (
        <div className={classes.root}>
            <div className={classes.scrollableContent}>
                <Stack gap="0.75rem" p="sm">
                    {/* Primary products - full cards */}
                    {primary.map((product, index) => (
                        <FullPlanCard
                            saleProgress={product.saleProgress}
                            onClick={() => {
                                handleCardClick(sessionId, product);
                            }}
                            key={product.productSku}
                            product={getProductInfo(sessionId, product.productSku)}
                            partner={partner}
                            variant="full"
                            isSelected={selectedProduct?.productSku === product.productSku}
                            position={index}
                            showEligibleTag={false}
                        />
                    ))}

                    {/* Secondary products - compact/full depending on chat mode */}
                    {secondary.length > 0 ? (
                        <SimpleGrid cols={2} spacing="lg" p={0}>
                            {secondary.map((product, index) => {
                                const variant = getProductCardVariant({ product, isChat });

                                return variant === "full" ? (
                                    <FullPlanCard
                                        saleProgress={product.saleProgress}
                                        onClick={() => {
                                            handleCardClick(sessionId, product);
                                            useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
                                        }}
                                        key={product.productSku}
                                        product={getProductInfo(sessionId, product.productSku)}
                                        partner={partner}
                                        variant="full"
                                        isSelected={selectedProduct?.productSku === product.productSku}
                                        position={index}
                                        showEligibleTag={isChat}
                                    />
                                ) : (
                                    <CompactPlanCard
                                        saleProgress={product.saleProgress}
                                        onClick={() => {
                                            handleCardClick(sessionId, product);
                                            useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
                                        }}
                                        key={product.productSku}
                                        product={getProductInfo(sessionId, product.productSku)}
                                        partner={partner}
                                        isSelected={selectedProduct?.productSku === product.productSku}
                                        productSku={product.productSku}
                                        position={index}
                                    />
                                );
                            })}
                        </SimpleGrid>
                    ) : showIneligibleText ? (
                        <Text>Bummer - this customer isn&apos;t eligible for any offers.</Text>
                    ) : null}

                    {/* Ineligible products */}
                    {ineligible.length > 0 && (
                        <>
                            <Flex align="center" gap="sm">
                                <Text size="xs" c="var(--mantine-color-dark-2)">
                                    Ineligible products
                                </Text>
                                <Divider size="xs" flex={1} color="var(--mantine-color-dark-4)" />
                            </Flex>

                            {ineligible.map((product) => (
                                <Text key={product.productSku} size="xs">
                                    {formatIneligibleProductDisplay({ product, partner })}
                                </Text>
                            ))}
                        </>
                    )}
                </Stack>
            </div>
            {shouldShowNoOfferButton({ partner, selectedProduct }) && <NoOfferButton />}
        </div>
    );
}
